/**
 * RecordViewContext — the state a record's header, body, and footer share.
 *
 * A record surface is three regions rendered by different owners: the chrome
 * renders the header and footer slots, the scroll region renders the body.
 * They still need to agree on one record and on a handful of UI state — the
 * header's "Edit" button toggles the overview's in-place editor, a body
 * section's CTA asks the notes section to open a composer, the overview's
 * edit form feeds the floating save bar. That state used to live inside the
 * body component, which is exactly why the header could not be hoisted out
 * of it (see docs/claude/REACT_19_PATTERNS.md § 1.3). It lives here now,
 * above all three regions.
 *
 * Data and actions are separate contexts (`createDataContext`): a footer
 * that only calls `requestNoteCreation` does not re-render when the object
 * updates.
 *
 * Host: `RecordSurfaceProvider` provides this once its resolution reaches
 * `ready`, with the store-backed (and preview-projected) object — for every
 * surface, the builder panel included.
 */

import { type ReactNode, useCallback, useMemo, useState } from 'react';

import { useAuthInfo } from '@/allauth_auth/hooks.js';
import { useUpdateCrmAssociation } from '@/lib/crm/hooks/useUpdateCrmAssociation';
import { createDataContext } from '@/lib/hooks/useDataContext';
import type { CrmObject } from '@/types/crm';

import { useAssociationMatchers } from '../../hooks/useAssociationMatchers';
import { isMemoEntityType } from '../memo/notes.utils';
import { TaskAssigneeMutationProvider } from '../task/TaskAssigneeMutationContext';
import { entityTypeHasTaskAssociation } from '../task/useHasTaskAssociation';
import type { RecordSectionSet, SaveBarState } from '../types';

/* eslint-disable react-refresh/only-export-components -- standard context pattern: provider and hooks co-located */

export type RecordViewObject = CrmObject & {
  associated_objects?: Record<string, CrmObject>;
};

export interface RecordViewData {
  object: RecordViewObject;
  /** Resolved section manifest — every flag present, defaults applied. */
  sections: RecordSectionSet;
  /**
   * Suppress the header's action bar — entity CTAs and the overflow menu.
   * For surfaces where the record is REVIEWED rather than operated on (the
   * builder panel), where the only action that belongs is the surface's own
   * Save.
   */
  hideActions: boolean;
  /**
   * Override the type chip label (e.g. "Automation") when the concrete type
   * is not yet known on the builder create path. Omit on all normal record
   * surfaces — the chip then shows the concrete type label.
   */
  typeLabelOverride?: string;
  /** Current save-bar state; the bar self-hides when null. */
  saveBarState: SaveBarState | null;
  /**
   * Header-driven edit for overview overrides that edit in place (ahoy_note →
   * NoteRecordOverview). The header button turns it on; the override reports
   * open/close back (click-to-edit on the note body, autosave-on-blur).
   */
  overviewEditMode: boolean;
  /** A body section asked the notes section to open its composer. */
  noteCreateRequested: boolean;
  /**
   * Notes are on for this surface AND a writable note association targets
   * this entity type. Gates both the notes section and the "Add note" CTA.
   */
  notesEnabled: boolean;
  /**
   * Tasks are on for this surface, a writable task association targets this
   * entity type, AND this entity type is not itself a memo/note record type
   * (suppressed via `!isMemoEntityType(entityTypeName)` — a note record
   * shows its own convert-to-task stage picker instead of a separate tasks
   * section). Gates the tasks section.
   */
  tasksEnabled: boolean;
}

export interface RecordViewActions {
  /** Producer channel for the save bar — passed to `OverviewSection`. */
  setSaveBarState: (state: SaveBarState | null) => void;
  setOverviewEditMode: (editing: boolean) => void;
  requestNoteCreation: () => void;
  noteCreateHandled: () => void;
}

const RecordView = createDataContext<RecordViewData, RecordViewActions>(
  'RecordView'
);

/** The record and its shared UI state. Throws outside a `RecordViewProvider`. */
export const useRecordView = RecordView.useData;
/** Stable actions on the shared UI state; never re-renders on data changes. */
export const useRecordViewActions = RecordView.useActions;

const ALL_SECTIONS: RecordSectionSet = {
  summary: true,
  typeSpecific: true,
  overview: true,
  notes: true,
  tasks: true,
  files: true,
  research: true,
  timeline: true,
};

export interface RecordViewProviderProps {
  object: RecordViewObject;
  /** Narrows what renders. Omitted entries default to `true`. */
  sections?: Partial<RecordSectionSet>;
  hideActions?: boolean;
  /** Override the type chip label; see `RecordViewData.typeLabelOverride`. */
  typeLabelOverride?: string;
  saveBarState: SaveBarState | null;
  onSaveStateChange: (state: SaveBarState | null) => void;
  children: ReactNode;
}

export function RecordViewProvider({
  object,
  sections,
  hideActions = false,
  typeLabelOverride,
  saveBarState,
  onSaveStateChange,
  children,
}: RecordViewProviderProps) {
  const resolvedSections = useMemo<RecordSectionSet>(
    () => ({ ...ALL_SECTIONS, ...sections }),
    [sections]
  );

  // Single shared mutation for the two task-kind-note owner-editing surfaces
  // (TaskRecordOverview's inline Assignee field and TaskActionFooter's
  // Reassign picker) — see TaskAssigneeMutationContext for why this must be
  // one instance rather than each surface calling useUpdateCrmAssociation.
  const { user } = useAuthInfo();
  const taskAssigneeMutation = useUpdateCrmAssociation(object.org_id, user?.id);

  const [noteCreateRequested, setNoteCreateRequested] = useState(false);
  const requestNoteCreation = useCallback(
    () => setNoteCreateRequested(true),
    []
  );
  const noteCreateHandled = useCallback(
    () => setNoteCreateRequested(false),
    []
  );

  const [overviewEditMode, setOverviewEditMode] = useState(false);
  // Edit mode must not leak across records when the surface moves to a
  // different entity (the builder panel goes from draft to saved record under
  // the same provider). Previous-value compare in render, not an effect —
  // docs/claude/REACT_19_PATTERNS.md § 4.2.
  const [editModeEntityId, setEditModeEntityId] = useState(object.entity_id);
  if (editModeEntityId !== object.entity_id) {
    setEditModeEntityId(object.entity_id);
    setOverviewEditMode(false);
  }

  // Uses `matchesTarget` because note→contact (and note→external_person) are
  // kind-based on `entity_kind=person`; a naive `target_entity_type_name`
  // check would silently miss them and hide notes in the contact slideout.
  const { associationTypes, matchesSource, matchesTarget } =
    useAssociationMatchers(object.org_id);
  const entityTypeName = object.entity_type_name;
  const hasNoteAssociation = useMemo(
    () =>
      associationTypes.some(
        at =>
          at.source_entity_type_name === 'ahoy_note' &&
          at.value_write_access !== 'internal' &&
          matchesTarget(at, entityTypeName)
      ),
    [associationTypes, matchesTarget, entityTypeName]
  );
  const notesEnabled = resolvedSections.notes && hasNoteAssociation;
  // Shared with the notes tab's "Add task" gate via `entityTypeHasTaskAssociation`
  // — one definition of "a writable ahoy_task association targets this record"
  // so the two never drift. See that helper for the kind-aware / transitive
  // matching rationale.
  const hasTaskAssociation = useMemo(
    () =>
      entityTypeHasTaskAssociation(
        associationTypes,
        matchesSource,
        matchesTarget,
        entityTypeName
      ),
    [associationTypes, matchesSource, matchesTarget, entityTypeName]
  );
  const tasksEnabled =
    resolvedSections.tasks &&
    hasTaskAssociation &&
    !isMemoEntityType(entityTypeName);

  const data = useMemo<RecordViewData>(
    () => ({
      object,
      sections: resolvedSections,
      hideActions,
      typeLabelOverride,
      saveBarState,
      overviewEditMode,
      noteCreateRequested,
      notesEnabled,
      tasksEnabled,
    }),
    [
      object,
      resolvedSections,
      hideActions,
      typeLabelOverride,
      saveBarState,
      overviewEditMode,
      noteCreateRequested,
      notesEnabled,
      tasksEnabled,
    ]
  );

  const actions = useMemo<RecordViewActions>(
    () => ({
      setSaveBarState: onSaveStateChange,
      setOverviewEditMode,
      requestNoteCreation,
      noteCreateHandled,
    }),
    [onSaveStateChange, requestNoteCreation, noteCreateHandled]
  );

  return (
    <RecordView.Provider data={data} actions={actions}>
      <TaskAssigneeMutationProvider mutation={taskAssigneeMutation}>
        {children}
      </TaskAssigneeMutationProvider>
    </RecordView.Provider>
  );
}
